/*!
 * Rover Quick-Search — tier-1 instant typeahead for RoverParts.eu
 * ----------------------------------------------------------------
 * A small, framework-free dropdown that attaches to the existing Rover search inputs
 * (#q-main / #q-results / #q-zero / #rover-sticky-q) and returns very confident, literal
 * matches against part codes and product names as the customer types. Pressing Enter (or
 * Search) hands off to the existing tier-2 AI search — window.Rover.search(query) — unchanged.
 *
 * Runs PARALLEL to SoBooster and is fully feature-flagged: if window.RoverQS.enabled is
 * false the whole component is inert, so SoBooster stays the instant search and nothing
 * regresses. Treats every query and the index blob strictly as data.
 *
 * Matching mirrors the box oracle (rover_engine/suggest.py) exactly so the in-browser path
 * and the /v1/suggest fallback rank identically. No external dependencies.
 */
(function () {
  "use strict";

  var CFG = window.RoverQS || {};
  if (CFG.enabled === false) return;                       // feature flag — instant kill switch

  /* Rover Phase 0b-QS — quick-search → order attribution. When the customer reached this page via a
     quick-search suggestion deep-link (choose() below appends ?rover_src=qs&rover_q=… to the product
     URL), promote those params onto the Shopify cart as attribution attributes so the resulting order
     is attributed to quick-search rather than the main finder. SINGLE-underscore keys (the store
     silently drops "__" keys — proven live). Sets the full attribution set, clearing the finder's
     _rover_qid/_rover_sid, so last-touch stays coherent when a visitor uses both tools. URL params are
     treated strictly as data; best-effort — never blocks the page. No-op on any non-QS arrival. */
  (function promoteRoverSrcFromUrl() {
    try {
      var params = new URLSearchParams(location.search);
      if (params.get("rover_src") !== "qs") return;
      var q = (params.get("rover_q") || "").slice(0, 120);
      fetch("/cart/update.js", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { _rover_src: "qs", _rover_q: q, _rover_qid: "", _rover_sid: "" } }),
        credentials: "same-origin"
      }).catch(function () { /* attribution is best-effort */ });
      // strip the params from the address bar so they don't linger, get bookmarked or shared
      params.delete("rover_src"); params.delete("rover_q");
      var qs = params.toString();
      history.replaceState(null, "", location.pathname + (qs ? "?" + qs : "") + location.hash);
    } catch (e) { /* attribution is best-effort */ }
  })();

  var BASE = (CFG.proxyBase || "/apps/rover").replace(/\/+$/, "");
  var INPUTS = CFG.inputSelector || "#q-main, #q-results, #q-zero, #rover-sticky-q";
  var LIMIT = Math.max(1, Math.min(CFG.limit || 8, 8));
  var DEBOUNCE = CFG.debounceMs || 150;
  var MIN_CHARS = CFG.minChars || 2;
  var MAX_SCAN = 5000;
  var FRAG_MIN = CFG.fragMin || 4;   // min length for infix (substring) code match — mirrors suggest.py FRAG_MIN
  var RECENT_KEY = "roverqs_recent";
  var RECENT_MAX = 6;
  // Search Resilience Phase 2 (2026-09-10): CDN copy of the blob (published by the box to Shopify Files,
  // URL read from shop metafield rover.suggest_blob by the snippet) + how long the box gets to answer.
  var BLOB_URL = CFG.blobUrl || null;
  var BLOB_GEN_CDN = +CFG.blobGen || 0;
  var BOX_TIMEOUT = CFG.boxTimeoutMs || 3000;
  var CACHE_NAME = "rover-qs-v1";
  var CACHE_REQ = "/__rover-qs/suggest-index";      // synthetic key inside our own Cache bucket

  // ---- i18n (EN/NL) ---------------------------------------------------------------------------
  var T = {
    en: { codes: "Part codes", products: "Products", recent: "Recent searches",
          popular: "Popular products", all: "Search all results for", in_stock: "In stock",
          low_stock: "Low stock", out_of_stock: "Out of stock" },
    nl: { codes: "Artikelcodes", products: "Producten", recent: "Recente zoekopdrachten",
          popular: "Populaire producten", all: "Zoek alle resultaten voor", in_stock: "Op voorraad",
          low_stock: "Weinig voorraad", out_of_stock: "Niet op voorraad" }
  };
  function lang() {
    if (CFG.lang === "en" || CFG.lang === "nl") return CFG.lang;
    var app = document.getElementById("rover-app");
    var l = (app && app.getAttribute("data-lang")) || document.documentElement.lang ||
            navigator.language || "en";
    return l.toLowerCase().indexOf("nl") === 0 ? "nl" : "en";
  }
  function t(k) { return (T[lang()] || T.en)[k]; }
  function stockLabel(state) { return t(state) || ""; }

  // ---- normalisation (identical to vocab.norm_code/base_code + suggest.code_key/fold) ----------
  function normCode(c) { return (c == null ? "" : String(c)).replace(/\s/g, "").toUpperCase(); }   // delimiter-SENSITIVE
  function codeKey(c) { return (c == null ? "" : String(c)).toUpperCase().replace(/[^A-Z0-9]/g, ""); }
  function baseCode(c) {
    var n = (c == null ? "" : String(c)).toUpperCase().replace(/\s/g, "").replace(/[^A-Z0-9]/g, "");
    return n.replace(/([0-9])[A-Z]$/, "$1");
  }
  function fold(s) {
    if (!s) return "";
    return String(s).normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
  }
  var TOK = /[a-z0-9]+/g;
  function toks(s) { return (fold(s).match(TOK) || []); }

  var KIND_PRIO = { code_primary: 0, code_alt: 1, code_variant: 2, code_prefix: 3,
                    code_fragment: 4, title_prefix: 5, title_token: 6, desc: 7 };
  function groupOf(kind) { return KIND_PRIO[kind] <= 4 ? "codes" : "products"; }
  function prio(kind) { return KIND_PRIO[kind] == null ? 99 : KIND_PRIO[kind]; }
  function stockRank(s) { return s === "in_stock" ? 0 : (s === "low_stock" ? 1 : 2); }

  // ---- in-browser index over the slim blob -----------------------------------------------------
  // Blob item keys: c=item_code(id+match) d=customer_code(display) t=title h=handle u=url k=codes[] p=price_display s=stock i=thumb v=pop
  var IDX = null;             // built index or null (then we use the server fallback)
  var BLOB_STATE = "idle";    // idle | loading | ready | error

  function buildIndex(items) {
    var norm = Object.create(null);    // normCode (delimiter-sensitive) -> [{i,tier}]
    var exact = Object.create(null);   // codeKey -> [{i,tier}]
    var base = Object.create(null);    // baseCode -> [i]
    var codeRows = [];                 // [key, i] sorted -> code prefix
    var codeInfix = [];                // [suffix, i] sorted -> code substring (infix) match, e.g. "a3138" in "GA3138"
    var titleRows = [];                // [ft, i] sorted -> title prefix
    var tokMap = Object.create(null);  // token -> [i]
    var enriched = new Array(items.length);
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var nic = codeKey(it.c);
      var seen = Object.create(null), seenN = Object.create(null);
      var codes = [it.c].concat(it.k || []);
      for (var j = 0; j < codes.length; j++) {
        var nn = normCode(codes[j]);
        var ck = codeKey(codes[j]);
        var tier = ck === nic ? 0 : 1;
        if (nn && !seenN[nn]) { seenN[nn] = 1; (norm[nn] || (norm[nn] = [])).push({ i: i, tier: tier }); }
        if (!ck || seen[ck]) continue;
        seen[ck] = 1;
        (exact[ck] || (exact[ck] = [])).push({ i: i, tier: tier });
        codeRows.push([ck, i]);
        // infix index: every PROPER suffix (start >= 1) of the folded code, length >= FRAG_MIN. A typed
        // fragment that is a prefix of one of these suffixes is a substring of the code, so "a3138" finds
        // "GA3138"/"DA3138". Position 0 is the prefix case (codeRows) — kept separate + higher priority.
        for (var si = 1; si <= ck.length - FRAG_MIN; si++) codeInfix.push([ck.slice(si), i]);
        var b = baseCode(codes[j]);
        if (b) (base[b] || (base[b] = [])).push(i);
      }
      var ft = fold(it.t || "");
      if (ft) {
        titleRows.push([ft, i]);
        var seent = Object.create(null), ts = ft.match(TOK) || [];
        for (var k = 0; k < ts.length; k++) {
          if (seent[ts[k]]) continue;
          seent[ts[k]] = 1;
          (tokMap[ts[k]] || (tokMap[ts[k]] = [])).push(i);
        }
      }
      enriched[i] = it;
    }
    codeRows.sort(cmp0);
    codeInfix.sort(cmp0);
    titleRows.sort(cmp0);
    var popular = items.map(function (_it, i) { return i; })
      .sort(function (a, b2) { return (items[b2].v || 0) - (items[a].v || 0); })
      .slice(0, 20);
    return { items: enriched, norm: norm, exact: exact, base: base, codeRows: codeRows,
             codeInfix: codeInfix,
             titleRows: titleRows, tokMap: tokMap, tokKeys: Object.keys(tokMap).sort(),
             popular: popular };
  }
  function cmp0(a, b) { return a[0] < b[0] ? -1 : (a[0] > b[0] ? 1 : 0); }

  function lowerBound(rows, key) {
    var lo = 0, hi = rows.length;
    while (lo < hi) { var m = (lo + hi) >> 1; if (rows[m][0] < key) lo = m + 1; else hi = m; }
    return lo;
  }
  function prefixScan(rows, key, cb) {
    if (!key) return;
    var i = lowerBound(rows, key), n = 0;
    while (i < rows.length && rows[i][0].lastIndexOf(key, 0) === 0) {
      cb(rows[i][1]); i++;
      if (++n >= MAX_SCAN) break;
    }
  }

  // Local matcher — mirrors suggest.suggest(). Returns ranked slim rows with match_kind/group.
  function matchLocal(q, limit) {
    if (!IDX) return null;
    q = (q || "").trim().slice(0, 64);
    if (q.length < MIN_CHARS) return [];
    var nn = normCode(q), nq = codeKey(q), fq = fold(q), qtoks = fq.match(TOK) || [];
    var best = Object.create(null);              // i -> kind
    function consider(i, kind) {
      var cur = best[i];
      if (cur === undefined || prio(kind) < prio(cur)) best[i] = kind;
    }
    // exact: delimiter-SENSITIVE first (retrieval parity), else delimiter-FOLDED (SoBooster fix)
    var ex = IDX.norm[nn] || IDX.exact[nq];
    if (ex) for (var a = 0; a < ex.length; a++) consider(ex[a].i, ex[a].tier === 0 ? "code_primary" : "code_alt");
    var hasExact = false;
    for (var bi in best) { if (prio(best[bi]) <= 1) { hasExact = true; break; } }
    if (!hasExact) {
      var bk = baseCode(q), arr = bk && IDX.base[bk];
      if (arr) for (var v = 0; v < arr.length; v++) consider(arr[v], "code_variant");
    }
    prefixScan(IDX.codeRows, nq, function (i) { consider(i, "code_prefix"); });
    // infix: typed code is a substring of a stored code (dropped prefix, e.g. "a3138" -> "GA3138").
    // Ranked below prefix, above title. Only for a query long enough to be specific (FRAG_MIN).
    if (nq.length >= FRAG_MIN) prefixScan(IDX.codeInfix, nq, function (i) { consider(i, "code_fragment"); });
    prefixScan(IDX.titleRows, fq, function (i) { consider(i, "title_prefix"); });
    tokenMatch(qtoks).forEach(function (i) { consider(i, "title_token"); });

    var out = [];
    for (var idx in best) out.push([parseInt(idx, 10), best[idx]]);
    out.sort(function (x, y) {
      var px = prio(x[1]), py = prio(y[1]);
      if (px !== py) return px - py;
      var nx = IDX.items[x[0]].n || 0, ny = IDX.items[y[0]].n || 0;   // most stock first (avoid back-orders)
      if (nx !== ny) return ny - nx;
      var vx = IDX.items[y[0]].v || 0, vy = IDX.items[x[0]].v || 0;
      if (vx !== vy) return vx - vy;
      return IDX.items[x[0]].c < IDX.items[y[0]].c ? -1 : 1;
    });
    return out.slice(0, limit || LIMIT).map(function (r) { return slim(IDX.items[r[0]], r[1]); });
  }
  function tokenMatch(qtoks) {
    if (!qtoks.length) return [];
    var sets = [], last = qtoks[qtoks.length - 1];
    for (var i = 0; i < qtoks.length - 1; i++) {
      var s = IDX.tokMap[qtoks[i]];
      if (!s) return [];
      sets.push(s);
    }
    var lastSet = Object.create(null), keys = IDX.tokKeys, j = lowerBound2(keys, last);
    while (j < keys.length && keys[j].lastIndexOf(last, 0) === 0) {
      IDX.tokMap[keys[j]].forEach(function (i) { lastSet[i] = 1; }); j++;
    }
    sets.push(Object.keys(lastSet).map(Number));
    var counts = Object.create(null), need = sets.length, res = [];
    sets.forEach(function (arr) {
      var seen = Object.create(null);
      arr.forEach(function (i) {
        if (seen[i]) return; seen[i] = 1;
        counts[i] = (counts[i] || 0) + 1;
        if (counts[i] === need) res.push(i);
      });
    });
    return res;
  }
  function lowerBound2(keys, key) {
    var lo = 0, hi = keys.length;
    while (lo < hi) { var m = (lo + hi) >> 1; if (keys[m] < key) lo = m + 1; else hi = m; }
    return lo;
  }
  function slim(it, kind) {
    // same key names as the server /v1/suggest rows, so optHTML renders one shape.
    // code = the customer-facing headline (blob `d` = customer_code); id stays item_code (`c`) for
    // identity/telemetry — matching still indexes `c` + all_codes, so the display change is render-only.
    return { id: it.c, code: it.d || it.c, title: it.t, handle: it.h, url: it.u,
             variant_id: it.w || null,          // Shopify variant (blob key `w`, since 2026-09-10) — box-down add-to-basket
             quality: it.q, alternative_codes: it.a || [],
             price_display: it.p, stock_state: it.s, stock_qty: (it.n === undefined ? null : it.n),
             eta: it.o ? { qty: it.o, date: it.e, confidence: it.f } : null,   // purchasing ETA (blob keys o/e/f, since 2026-10-08)
             stock_label: stockLabel(it.s), thumb: it.i, match_kind: kind, group: groupOf(kind) };
  }

  // ---- blob load: cache -> box -> CDN (Search Resilience Phase 2, 2026-09-10) ---------------------
  // Until this round the blob lived only on the box, so a service restart killed the dropdown and the
  // instant-matches strip together with the full search. Order of truth now:
  //   1. the Cache API copy from a previous visit paints instantly (stale-while-revalidate);
  //   2. the box copy is freshest (stock within the 30s hot-swap) and revalidates it — but gets only
  //      BOX_TIMEOUT ms, so a dead box costs one short wait, not a hung dropdown;
  //   3. if the box is unreachable, the CDN copy the box last published (<= 15 min old) takes over,
  //      served raw-gzip and inflated here with DecompressionStream.
  // Whichever wins is written back to the cache when it is a NEWER generation; an older generation
  // never replaces a newer index. A box failure flips CFG.degraded so the other box calls (/v1/stock,
  // the /v1/suggest fallback) are skipped for this page load instead of each waiting out their own
  // timeout. Every step is best-effort and nothing here can throw out of the promise chain.
  var IDX_GEN = 0, BLOB_SOURCE = null, BOX_OK = null;    // BOX_OK: did the box answer the blob fetch this page load
  CFG.degraded = false;
  // Keystrokes that arrive before the index is built go to the server fallback (or, degraded, paint
  // only the "search all" row). When the index then lands, every open dropdown re-runs its query so
  // the customer never sits on a stale empty panel — proven necessary at Gate 2 (box down, cache
  // parse ~300 ms, CDN path ~2 s: the first query stayed empty until the next keystroke).
  var READY_CBS = [];
  function onIndexReady(fn) { READY_CBS.push(fn); }
  function fireIndexReady() {
    for (var i = 0; i < READY_CBS.length; i++) { try { READY_CBS[i](); } catch (e) {} }
  }

  function timeoutSignal(ms, parent) {
    // parent + timeout combined where the platform can (Chrome 116+/Safari 17.4+); else parent only
    try {
      var t = AbortSignal.timeout(ms);
      if (parent && typeof AbortSignal.any === "function") return AbortSignal.any([parent, t]);
      return parent || t;
    } catch (e) { return parent || undefined; }
  }
  // (generation, schema) ordering: a newer generation always wins; the same generation wins only with a
  // newer schema (the box bumps `schema` when item keys change without a rebuild, e.g. +variant id).
  var IDX_SCHEMA = 0;
  function newer(data) {
    var gen = +data.generation || 0, sch = +data.schema || 1;
    return !IDX || gen > IDX_GEN || (gen === IDX_GEN && sch > IDX_SCHEMA);
  }
  function adopt(data, source) {
    if (!data || !Array.isArray(data.items)) return false;
    if (!newer(data)) return false;
    IDX = buildIndex(data.items); IDX_GEN = +data.generation || 0; IDX_SCHEMA = +data.schema || 1;
    BLOB_SOURCE = source; BLOB_STATE = "ready";
    fireIndexReady();
    return true;
  }
  function cacheOpen() {
    try {
      if (!window.caches || location.protocol !== "https:") return Promise.resolve(null);
      return caches.open(CACHE_NAME).catch(function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }
  function cacheRead() {
    return cacheOpen().then(function (c) { return c ? c.match(CACHE_REQ) : null; })
      .then(function (r) { return r ? r.json() : null; })
      .catch(function () { return null; });
  }
  function cacheWrite(data, text) {
    var gen = +data.generation || 0, sch = +data.schema || 1;
    cacheOpen().then(function (c) {
      if (!c) return;
      return c.match(CACHE_REQ).then(function (r) {
        var haveGen = r ? +(r.headers.get("X-Rover-Generation") || 0) : 0;
        var haveSch = r ? +(r.headers.get("X-Rover-Schema") || 1) : 0;
        if (gen < haveGen || (gen === haveGen && sch <= haveSch)) return;
        return c.put(CACHE_REQ, new Response(text || JSON.stringify(data), {
          headers: { "Content-Type": "application/json", "X-Rover-Generation": String(gen), "X-Rover-Schema": String(sch) }
        }));
      });
    }).catch(function () {});
  }
  function fetchBox() {
    return fetch(BASE + "/v1/suggest/index", { credentials: "same-origin", signal: timeoutSignal(BOX_TIMEOUT) })
      .then(function (r) { if (!r.ok) throw new Error("blob " + r.status); return r.text(); })
      .then(function (text) { return { data: JSON.parse(text), text: text }; });
  }
  function inflate(buf) {
    var u8 = new Uint8Array(buf);
    if (!(u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b)) return Promise.resolve(new TextDecoder().decode(u8));
    if (typeof DecompressionStream !== "function") return Promise.reject(new Error("no DecompressionStream"));
    return new Response(new Blob([u8]).stream().pipeThrough(new DecompressionStream("gzip"))).text();
  }
  function fetchCdn() {
    if (!BLOB_URL) return Promise.reject(new Error("no cdn copy"));
    return fetch(BLOB_URL, { credentials: "omit", signal: timeoutSignal(20000) })
      .then(function (r) { if (!r.ok) throw new Error("cdn " + r.status); return r.arrayBuffer(); })
      .then(inflate)
      .then(function (text) { return { data: JSON.parse(text), text: text }; });
  }
  function loadBlob() {
    if (BLOB_STATE === "loading" || BLOB_STATE === "ready") return;
    BLOB_STATE = "loading";
    cacheRead().then(function (cached) {
      if (cached) adopt(cached, "cache");
      return fetchBox().then(function (res) {
        BOX_OK = true;
        if (adopt(res.data, "box")) cacheWrite(res.data, res.text);
      }, function () {
        BOX_OK = false; CFG.degraded = true;         // box unreachable or slow: stop asking it this page load
        if (cached && (+cached.generation || 0) >= BLOB_GEN_CDN) return;   // cache is as fresh as the CDN copy
        return fetchCdn().then(function (res) {
          if (adopt(res.data, "cdn")) cacheWrite(res.data, res.text);
        });
      });
    }).catch(function () {}).then(function () {
      if (BLOB_STATE !== "ready") BLOB_STATE = "error";   // nothing loaded anywhere: server fallback (if not degraded)
    });
  }

  // ---- server fallback (and pre-blob path) -----------------------------------------------------
  function matchServer(q, signal) {
    if (CFG.degraded) return Promise.resolve([]);        // the box is not answering; don't queue on it
    return fetch(BASE + "/v1/suggest", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q: q, limit: LIMIT, lang: lang() }),
      credentials: "same-origin", signal: timeoutSignal(BOX_TIMEOUT, signal)
    }).then(function (r) { return r.ok ? r.json() : { results: [] }; })
      .then(function (d) { return (d && d.results) || []; });
  }

  // ---- live stock overlay ----------------------------------------------------------------------
  // The blob's stock number ages with the snapshot (10-min delta build + blob caches), while the
  // product page shows Shopify's live figure — so the two could disagree. When rows are actually
  // SHOWN, fetch the live number for just those items (/v1/stock, <=8 ids) and patch the labels in
  // place. Never re-ranks an open dropdown; a tiny client cache stops refetch churn while typing;
  // any failure silently keeps the snapshot text.
  var STOCK_TTL = 30000;
  var STOCK_STATES = { in_stock: 1, low_stock: 1, out_of_stock: 1 };
  var stockCache = Object.create(null);   // id -> {qty, state, eta, ts}
  var stockCtrl = null;
  function fetchLiveStock(ids, apply) {
    var need = [], now = Date.now();
    for (var i = 0; i < ids.length; i++) {
      var c = stockCache[ids[i]];
      if (c && now - c.ts < STOCK_TTL) apply(ids[i], c);
      else need.push(ids[i]);
    }
    if (!need.length) return;
    if (CFG.degraded) return;                            // box down: the snapshot number already shown stands
    if (stockCtrl) stockCtrl.abort();
    stockCtrl = ("AbortController" in window) ? new AbortController() : null;
    var my = stockCtrl;
    fetch(BASE + "/v1/stock", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: need }), credentials: "same-origin",
      signal: timeoutSignal(BOX_TIMEOUT, my && my.signal)
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.stocks || stockCtrl !== my) return;
        var ts = Date.now();
        for (var id in d.stocks) {
          var s = d.stocks[id];
          if (!s || !STOCK_STATES[s.state]) continue;             // whitelist — state feeds a class name
          if (s.source === "live") stockCache[id] = { qty: s.qty, state: s.state, eta: s.eta, ts: ts };
          apply(id, s);
        }
      }).catch(function () {});
  }

  // ---- telemetry (best-effort beacon; never blocks) --------------------------------------------
  function beacon(payload) {
    try {
      fetch(BASE + "/v1/suggest/log", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload), credentials: "same-origin", keepalive: true
      }).catch(function () {});
    } catch (e) {}
  }

  // ---- recent searches (localStorage) ----------------------------------------------------------
  function recent() { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; } }
  function pushRecent(q) {
    q = (q || "").trim(); if (!q) return;
    var r = recent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    r.unshift(q); try { localStorage.setItem(RECENT_KEY, JSON.stringify(r.slice(0, RECENT_MAX))); } catch (e) {}
  }

  // ---- DOM helpers -----------------------------------------------------------------------------
  function el(tag, attrs, html) {
    var e = document.createElement(tag);
    if (attrs) for (var k in attrs) e.setAttribute(k, attrs[k]);
    if (html != null) e.innerHTML = html;
    return e;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  // Purchasing ETA sentence for an out-of-stock item with free inbound stock. Same wording as the
  // product page (snippets/bp-eta.liquid): confirmed = the date, estimated = around the date,
  // anything else = date to be confirmed.
  var ETA_MONTHS = { en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
                     nl: ["jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec"] };
  function etaText(eta) {
    var nl = lang() === "nl", p = (eta.date || "").split("-");
    var day = p.length === 3 ? (+p[2]) + " " + ETA_MONTHS[nl ? "nl" : "en"][p[1] - 1] : "";
    if (day && eta.confidence === "confirmed") return (nl ? "Verwacht op voorraad " : "Expected in stock ") + day;
    if (day && eta.confidence === "estimated") return (nl ? "Verwacht op voorraad rond " : "Expected in stock around ") + day;
    return nl ? "Besteld, leverdatum volgt" : "On order, delivery date to be confirmed";
  }
  // stock as a unit count: "34 in stock" / "No stock" (falls back to the state label if qty unknown);
  // out of stock with free inbound stock reads as the ETA sentence instead of "No stock"
  function stockText(n, state, eta) {
    var l = lang();
    if (n != null && n > 0) return n + (l === "nl" ? " op voorraad" : " in stock");
    if ((n === 0 || state === "out_of_stock") && eta) return etaText(eta);
    if (n === 0 || state === "out_of_stock") return l === "nl" ? "Geen voorraad" : "No stock";
    return stockLabel(state);
  }
  // Highlight the matched portion of a code: find the (delimiter-folded) query as a substring of the
  // (delimiter-folded) code, then wrap the corresponding span of the ORIGINAL code — delimiters included —
  // in <b class="rqs-hit">. Returns escaped HTML. So searching "3340" lights up "3340" inside "ERR3340",
  // and "5806hd" lights up "5806H/D" inside "FRC5806H/D". No match (or empty query) -> plain escaped code.
  function highlightCode(codeStr, query) {
    var s = String(codeStr == null ? "" : codeStr);
    var fq = codeKey(query);
    if (!fq) return esc(s);
    var fc = codeKey(s), pos = fc.indexOf(fq);
    if (pos < 0) return esc(s);
    var end = pos + fq.length, fi = 0, a = -1, b = -1;
    for (var i = 0; i < s.length; i++) {
      if (/[A-Za-z0-9]/.test(s[i])) {
        if (fi === pos) a = i;
        if (fi === end - 1) b = i;
        fi++;
      }
    }
    if (a < 0 || b < 0) return esc(s);
    return esc(s.slice(0, a)) + '<b class="rqs-hit">' + esc(s.slice(a, b + 1)) + "</b>" + esc(s.slice(b + 1));
  }
  // "also sold as" codes under the primary; the matched part of each is highlighted (like tier-2).
  // Skips any alt equal to the primary code shown above.
  function altHTML(alts, query, primary) {
    if (!alts || !alts.length) return "";
    var pk = codeKey(primary), out = [];
    for (var i = 0; i < alts.length; i++) {
      if (codeKey(alts[i]) === pk) continue;
      out.push('<span class="rqs-alt">' + highlightCode(alts[i], query) + "</span>");
    }
    return out.length ? '<span class="rqs-alts">' + out.join("") + "</span>" : "";
  }

  // ---- the dropdown controller, one per enhanced input -----------------------------------------
  var UID = 0;
  function enhance(input) {
    if (input.__roverQS) return;
    input.__roverQS = true;
    var id = "rqs-" + (++UID);
    var panel = el("div", { class: "rqs-panel", id: id + "-lb", role: "listbox", "aria-label": "Suggestions" });
    panel.style.display = "none";
    document.body.appendChild(panel);
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-expanded", "false");
    input.setAttribute("aria-controls", id + "-lb");
    input.setAttribute("autocomplete", "off");

    var rows = [];          // [{type:'option', data}|{type:'all'}] flat, navigable
    var active = -1;
    // How `active` was set. Hover highlights a row for visual feedback, but ONLY a keyboard
    // selection (arrow keys) makes that row Enter-selectable — otherwise a cursor resting over
    // the panel silently turns Enter from "run the full search" into "open this product".
    // The panel re-renders on every debounced keystroke, so mouseenter can fire with no mouse
    // movement at all when a row grows under a stationary cursor.
    var activeVia = "mouse"; // 'key' | 'mouse'
    var lastResults = [];   // for telemetry
    var lastSource = "local";
    var timer = null, ctrl = null, open = false, escalated = false;

    function position() {
      var r = input.getBoundingClientRect();
      panel.style.position = "absolute";
      panel.style.left = (r.left + window.scrollX) + "px";
      panel.style.top = (r.bottom + window.scrollY + 4) + "px";
      panel.style.width = Math.max(r.width, 280) + "px";
      panel.style.zIndex = 99999;
    }
    function show() { if (!open) { open = true; position(); panel.style.display = "block"; input.setAttribute("aria-expanded", "true"); } }
    function hide() {
      if (!open) return;
      open = false; panel.style.display = "none"; input.setAttribute("aria-expanded", "false");
      input.removeAttribute("aria-activedescendant"); active = -1;
      // a late server-fallback response must never reopen a panel the customer just closed/submitted
      if (ctrl) { ctrl.abort(); ctrl = null; }
    }

    function renderEmptyState() {
      var rec = recent(), pop = (IDX && IDX.popular.slice(0, 6).map(function (i) { return slim(IDX.items[i], "title_token"); })) || [];
      if (!rec.length && !pop.length) { hide(); return; }
      var html = "", flat = [];
      if (rec.length) {
        html += '<div class="rqs-group" role="presentation">' + esc(t("recent")) + "</div>";
        rec.forEach(function (q) {
          html += '<div class="rqs-opt rqs-recent" role="option" id="' + id + "-o" + flat.length + '">' +
                  '<span class="rqs-ico" aria-hidden="true">↩</span><span class="rqs-t">' + esc(q) + "</span></div>";
          flat.push({ type: "recent", q: q });
        });
      }
      if (pop.length) {
        html += '<div class="rqs-group" role="presentation">' + esc(t("popular")) + "</div>";
        pop.forEach(function (p) { html += optHTML(p, flat.length, ""); flat.push({ type: "option", data: p }); });
      }
      panel.innerHTML = html; rows = flat; active = -1; bindRows(); show();
      overlayStockFromRows();
    }

    // patch the visible rows' stock text with the live number (display only — never re-ranks)
    function overlayStockFromRows() {
      var ids = [];
      for (var i = 0; i < rows.length; i++) if (rows[i].type === "option" && rows[i].data && rows[i].data.id) ids.push(rows[i].data.id);
      if (!ids.length) return;
      fetchLiveStock(ids, function (id, s) {
        for (var j = 0; j < rows.length; j++) {
          var r = rows[j];
          if (r.type !== "option" || !r.data || r.data.id !== id) continue;
          r.data.stock_qty = s.qty; r.data.stock_state = s.state; r.data.eta = s.eta;   // keep row data honest for reuse
          var opt = panel.querySelector('.rqs-opt[data-i="' + j + '"]');
          if (!opt) continue;
          var node = opt.querySelector(".rqs-stock");
          if (!node) {
            var sub = opt.querySelector(".rqs-sub");
            if (!sub) continue;
            node = document.createElement("span");
            sub.appendChild(node);
          }
          node.className = "rqs-stock rqs-" + s.state;
          node.textContent = stockText(s.qty, s.state, s.eta);
        }
      });
    }

    function optHTML(p, i, query) {
      var thumb = p.thumb ? '<img class="rqs-thumb" src="' + esc(p.thumb) + '" alt="" loading="lazy">'
                          : '<span class="rqs-thumb rqs-thumb-empty" aria-hidden="true"></span>';
      var code = '<span class="rqs-code">' + highlightCode(p.code, query) + "</span>";
      var qual = p.quality ? '<span class="rqs-qual rqs-qual-' + esc(String(p.quality).toLowerCase()) + '">' + esc(p.quality) + "</span>" : "";
      var stock = p.stock_state ? '<span class="rqs-stock rqs-' + esc(p.stock_state) + '">' + esc(stockText(p.stock_qty, p.stock_state, p.eta)) + "</span>" : "";
      var price = p.price_display ? '<span class="rqs-price">' + esc(p.price_display) + "</span>" : "";
      var alts = altHTML(p.alternative_codes, query, p.code);
      return '<div class="rqs-opt" role="option" id="' + id + "-o" + i + '" data-i="' + i + '">' +
             thumb + '<span class="rqs-body"><span class="rqs-t">' + esc(p.title || p.code) + "</span>" +
             '<span class="rqs-sub">' + code + qual + stock + "</span>" + alts + "</span>" + price + "</div>";
    }

    function renderResults(results, query) {
      lastResults = results; rows = [];
      if (!results.length) {
        // still offer the "search all" escalation row
        panel.innerHTML = allRowHTML(query, 0);
        rows = [{ type: "all", q: query }]; active = -1; bindRows(); show(); return;
      }
      var html = "", flat = [], lastGroup = null;
      results.forEach(function (p) {
        if (p.group !== lastGroup) {
          lastGroup = p.group;
          html += '<div class="rqs-group" role="presentation">' + esc(t(p.group)) + "</div>";
        }
        html += optHTML(p, flat.length, query); flat.push({ type: "option", data: p });
      });
      html += allRowHTML(query, flat.length); flat.push({ type: "all", q: query });
      panel.innerHTML = html; rows = flat; active = -1; bindRows(); show();
      overlayStockFromRows();
    }
    function allRowHTML(query, i) {
      return '<div class="rqs-opt rqs-all" role="option" id="' + id + "-o" + i + '" data-i="' + i + '">' +
             '<span class="rqs-ico" aria-hidden="true">⌕</span><span class="rqs-t">' +
             esc(t("all")) + ' “' + esc(query) + '”</span></div>';
    }

    function bindRows() {
      var opts = panel.querySelectorAll(".rqs-opt");
      for (var i = 0; i < opts.length; i++) {
        (function (node, i) {
          node.addEventListener("mousedown", function (e) { e.preventDefault(); choose(i); });
          node.addEventListener("mouseenter", function () { setActive(i, "mouse"); });
        })(opts[i], i);
      }
    }
    function setActive(i, via) {
      var opts = panel.querySelectorAll(".rqs-opt");
      if (active >= 0 && opts[active]) opts[active].classList.remove("rqs-active");
      active = i; activeVia = via || "mouse";
      if (i >= 0 && opts[i]) { opts[i].classList.add("rqs-active"); input.setAttribute("aria-activedescendant", opts[i].id); }
      else input.removeAttribute("aria-activedescendant");
    }
    function choose(i) {
      var row = rows[i]; if (!row) return;
      if (row.type === "recent") { input.value = row.q; run(row.q); return; }
      if (row.type === "all") { escalate(row.q); return; }
      var p = row.data;
      pushRecent(input.value);
      beacon({ query_text: input.value, char_count: (input.value || "").length, returned_count: lastResults.length,
               source: lastSource, outcome: "suggestion_click", outcome_item: p.id, match_kind: p.match_kind, lang: lang() });
      if (p.url) {                                          // deep-link to the product, tagged for QS attribution
        var sep = p.url.indexOf("?") === -1 ? "?" : "&";
        window.location.assign(p.url + sep + "rover_src=qs&rover_q=" + encodeURIComponent((input.value || "").slice(0, 120)));
      }
    }
    function escalate(q) {
      escalated = true; pushRecent(q);
      beacon({ query_text: q, char_count: (q || "").length, returned_count: lastResults.length,
               source: lastSource, outcome: "escalate_search", match_kind: lastResults[0] && lastResults[0].match_kind, lang: lang() });
      hide();
      // On the homepage the on-page Rover takes the query; on every OTHER page window.Rover doesn't
      // exist, so hand off exactly like the site-wide bar's native submit: home + ?rover_q=…
      if (window.Rover && typeof window.Rover.search === "function") window.Rover.search(q);
      else window.location.assign("/?rover_q=" + encodeURIComponent(q));
    }

    function run(query) {
      query = query || "";
      if (query.trim().length < MIN_CHARS) { renderEmptyState(); return; }
      loadBlob();                                           // no-op once loading/ready; covers typing without a focus event
      var local = matchLocal(query);
      if (local !== null) {
        if (ctrl) { ctrl.abort(); ctrl = null; }          // a pre-index server fallback must not overpaint local rows
        lastSource = "local"; renderResults(local, query);
        // Zero literal hits on a word-like query (>= 3 alpha chars): the blob can't do synonyms,
        // so escalate to the box oracle — its server-only lexicon tier resolves NL terms
        // ("achterlicht" -> rear lamps) with the SAME lexicon as the full search. The "search all"
        // row is already painted, so a slow / failed / empty server answer costs nothing.
        if (!local.length && /[a-z]{3,}/.test(fold(query))) {
          if (ctrl) ctrl.abort();
          ctrl = ("AbortController" in window) ? new AbortController() : null;
          var lexCtrl = ctrl;
          matchServer(query, ctrl && ctrl.signal).then(function (results) {
            if (ctrl !== lexCtrl || !results.length) return;
            lastSource = "server"; renderResults(results, query);
          }).catch(function () {});
        }
        return;
      }
      // blob not ready -> server fallback, cancel any stale request
      if (ctrl) ctrl.abort();
      ctrl = ("AbortController" in window) ? new AbortController() : null;
      var myCtrl = ctrl;
      matchServer(query, ctrl && ctrl.signal).then(function (results) {
        if (ctrl !== myCtrl) return;                        // a newer request superseded this one
        lastSource = "server"; renderResults(results, query);
      }).catch(function () {});
    }

    input.addEventListener("focus", function () { loadBlob(); if (!input.value.trim()) renderEmptyState(); else run(input.value); });
    // index landed after the customer started typing: repaint this panel from the local matcher
    onIndexReady(function () {
      if (open && document.activeElement === input && input.value.trim().length >= MIN_CHARS) run(input.value);
    });
    input.addEventListener("input", function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { run(input.value); }, DEBOUNCE);
    });
    input.addEventListener("keydown", function (e) {
      if (!open) { if (e.key === "ArrowDown") { run(input.value); } return; }
      var n = rows.length;
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(active + 1 >= n ? 0 : active + 1, "key"); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive(active - 1 < 0 ? n - 1 : active - 1, "key"); }
      else if (e.key === "Enter") {
        // Only an arrow-key selection opens a product. A merely hovered row must not hijack Enter.
        if (active >= 0 && activeVia === "key") { e.preventDefault(); choose(active); }
        else { hide(); }                                    // let the form submit -> tier-2 (logged below)
      } else if (e.key === "Escape") { hide(); }
    });
    input.addEventListener("blur", function () { setTimeout(hide, 120); });

    var form = input.closest && input.closest("form");
    if (form) form.addEventListener("submit", function () {
      if (open && !escalated && rows.length) {
        beacon({ query_text: input.value, char_count: (input.value || "").length, returned_count: lastResults.length,
                 source: lastSource, outcome: "escalate_search", match_kind: lastResults[0] && lastResults[0].match_kind, lang: lang() });
        pushRecent(input.value);
      }
      escalated = false; hide();
    });
    window.addEventListener("scroll", function () { if (open) position(); }, true);
    window.addEventListener("resize", function () { if (open) position(); });
  }

  // ---- delegated init: enhance matching inputs on first focus (covers late-inserted sticky) -----
  function maybeEnhance(node) {
    if (node && node.matches && node.matches(INPUTS)) enhance(node);
  }
  document.addEventListener("focusin", function (e) { maybeEnhance(e.target); }, true);
  // also enhance any already in the DOM at load
  function initExisting() {
    var list = document.querySelectorAll(INPUTS);
    for (var i = 0; i < list.length; i++) enhance(list[i]);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initExisting);
  else initExisting();

  // Home page: the Part Finder is front and centre, so warm the blob immediately instead of waiting
  // for the first input focus — the first dropdown interaction and the quick-hits strip (including
  // ?rover_q= hand-off landings from the site-wide bar) are then instant.
  if (document.getElementById("rover-app")) loadBlob();

  // ---- public read-only hooks (rover-finder.js) ------------------------------------------------
  // match(q, limit): instant tier-1 rows from the SAME in-browser index — rover-finder paints them
  // as "instant matches" the moment a full search starts. Returns null while the blob isn't ready
  // (and kicks off the load so a retry can hit). liveStock/stockText: the finder patches its result
  // cards through the SAME /v1/stock cache, so both surfaces show one number.
  window.RoverQS = CFG;
  CFG.match = function (q, limit) {
    if (!IDX) { loadBlob(); return null; }
    var n = Math.max(1, Math.min(limit || 4, 24));    // up to 24: the box-down results page (Phase 4)
    return matchLocal(q, n);
  };
  CFG.liveStock = fetchLiveStock;
  CFG.stockText = stockText;
  // Search Resilience Phase 2: where the index came from + whether the box answered this page load.
  // rover-finder.js reads blobState() to decide its box-down results page and calls markDegraded()
  // when its own /v1/search fails or times out, so both surfaces stop waiting on a dead box together.
  CFG.blobState = function () {
    return { state: BLOB_STATE, source: BLOB_SOURCE, generation: IDX_GEN, schema: IDX_SCHEMA, boxOk: BOX_OK,
             degraded: !!CFG.degraded, cdn: !!BLOB_URL, cdnGeneration: BLOB_GEN_CDN };
  };
  CFG.markDegraded = function () { CFG.degraded = true; };
})();